// Turning a click into a question. A click on a bar, a slice, a ranked row, a heatmap cell or a table row becomes a
// Pick: the filters that identify what was clicked (a category, a customer, a month), the value it shows, and a
// label. From a Pick and the tile's own query come the two things the inspector asks the API: why the number moved
// (an Explain request) and which ledger rows make it up (a table query). Nothing here computes a number.
import type { DSpec, DTile, XDataset, XExplainRequest, XFilter, XOutColumn, XQuery, XResult, XPeriod } from "../api";
import { fmtKey } from "./format";

export type Pick = {
  tileId: string;
  label: string; // "STAMP · Aug 2026"
  filters: XFilter[]; // what was clicked, on row columns
  range?: { from: string; to: string; column: string } | null; // a clicked date bucket
  value: number; // which of the tile's values (series)
  shown?: number | null; // the number on the mark
  cross?: { column: string; value: unknown; name: string } | null; // a text member the dashboard can be filtered to
};

const iso = (d: Date) => d.toISOString().slice(0, 10);
/** The first and last day of a date bucket. */
export function bucketRange(key: string, bucket?: string) {
  const d = new Date(key.slice(0, 10) + "T00:00:00Z");
  const end = new Date(d);
  if (bucket === "week") end.setUTCDate(end.getUTCDate() + 6);
  else if (bucket === "month") end.setUTCMonth(end.getUTCMonth() + 1, 0);
  else if (bucket === "quarter") end.setUTCMonth(end.getUTCMonth() + 3, 0);
  else if (bucket === "year") end.setUTCFullYear(end.getUTCFullYear() + 1, 0, 0);
  return { from: iso(d), to: iso(end) };
}

/** The filter that picks one key of a grouping column (or the range of a date bucket). */
function keyPart(col: XOutColumn, key: unknown): { filter?: XFilter; range?: Pick["range"]; label: string; cross?: Pick["cross"] } {
  const label = fmtKey(key, col);
  if (col.bucket && typeof key === "string" && /^\d{4}-\d{2}-\d{2}/.test(key)) return { range: { ...bucketRange(key, col.bucket), column: col.id }, label };
  if (key === null || key === undefined || key === "") return { filter: { column: col.id, op: "empty" }, label };
  const cross = col.type === "text" ? { column: col.id, value: key, name: col.name } : null;
  return { filter: { column: col.id, op: "in", value: [key] }, label, cross };
}

/** A Pick from the keys of a grouped row or pivot cell. */
export function pickOf(tileId: string, cols: XOutColumn[], keys: unknown[], value: number, shown?: number | null): Pick {
  const parts = cols.map((c, i) => keyPart(c, keys[i]));
  return {
    tileId, value, shown, label: parts.map((p) => p.label).join(" · "),
    filters: parts.flatMap((p) => (p.filter ? [p.filter] : [])),
    range: parts.find((p) => p.range)?.range ?? null,
    cross: parts.length === 1 ? parts[0].cross ?? null : parts.find((p) => p.cross)?.cross ?? null,
  };
}

/** The group columns and keys behind a chart's x position and series (grouped or pivot results). */
export function pickFromChart(tileId: string, res: XResult, x: unknown[], seriesKeys: (unknown[] | null)[], i: number, k: number, shown?: number | null): Pick | null {
  if (res.kind === "grouped") return pickOf(tileId, [res.groups[0]], [x[i]], Math.min(k, res.values.length - 1), shown);
  if (res.kind === "pivot") {
    const ck = seriesKeys[k];
    if (!ck) return null; // "Other"
    return pickOf(tileId, [res.rowGroups[0], ...res.colGroups], [x[i], ...ck], 0, shown);
  }
  return null;
}

const COMPARE: Record<string, "py" | "pp"> = { py: "py", pp: "pp" };

/** What to send Explain for a tile (optionally narrowed to a pick, and to members drilled into). */
/** compare: py or pp; undefined uses the dashboard's (prior year if it has none); null asks for no comparison. */
export function explainRequest(tile: DTile, main: XQuery, spec: DSpec, pick: Pick | null, drill: XFilter[], compare?: "py" | "pp" | null, name?: string): XExplainRequest | null {
  const v = main.values?.[pick?.value ?? 0] ?? main.values?.[0];
  if (!v) return null;
  const date = tile.date ?? null;
  // The pick's date range replaces the dashboard period on the same column.
  const filters = [...(main.filters ?? []).filter((f) => !(pick?.range && f.column === pick.range.column && (f.op === "period" || f.op === "between"))), ...(pick?.filters ?? []), ...drill];
  const range = pick?.range && pick.range.column === date ? { from: pick.range.from, to: pick.range.to } : null;
  if (pick?.range && pick.range.column !== date) filters.push({ column: pick.range.column, op: "between", value: pick.range.from, value2: pick.range.to });
  return {
    dataset: main.dataset, calcs: main.calcs, value: v, filters, date,
    period: range ? null : (spec.period as XPeriod | null), range,
    compare: compare === null ? undefined : compare ?? COMPARE[spec.compare] ?? "py",
    ...(name ? { name } : {}),
  };
}

/** The rows behind a number: the same filters, as a plain table, newest first. */
export function entriesQuery(tile: DTile, main: XQuery, ds: XDataset | undefined, pick: Pick | null, drill: XFilter[], extra: XFilter[] = []): XQuery {
  const date = tile.date ?? ds?.columns.find((c) => c.type === "date")?.id;
  const filters = [...(main.filters ?? []).filter((f) => !(pick?.range && f.column === pick.range.column && (f.op === "period" || f.op === "between"))), ...(pick?.filters ?? []), ...drill, ...extra];
  if (pick?.range) filters.push({ column: pick.range.column, op: "between", value: pick.range.from, value2: pick.range.to });
  // The columns an accountant would want: the date, the document, who and what (names, with the codes that link to
  // BC), then the amounts. Columns the click already pins to one value are left out; they'd say the same on every row.
  const cols0 = ds?.columns ?? [];
  const pinned = new Set(filters.filter((f) => f.op === "eq" || (f.op === "in" && Array.isArray(f.value) && f.value.length === 1)).map((f) => f.column));
  const labelOf = ds?.labels ?? {};
  for (const [code, name] of Object.entries(labelOf)) { if (pinned.has(name)) pinned.add(code); if (pinned.has(code)) pinned.add(name); }
  const skip = new Set(["entry_no", "id", ...pinned]);
  const has = (id: string) => cols0.some((c) => c.id === id) && !skip.has(id);
  const first = [date, "document_type", "document_no", "number", "contract_no", "release_no", "quote_no"].filter((x): x is string => !!x && has(x));
  const texts = cols0.filter((c) => (c.type === "text" || c.type === "bool") && !first.includes(c.id) && !skip.has(c.id)).map((c) => c.id);
  const nums = cols0.filter((c) => c.type === "number" && !skip.has(c.id)).map((c) => c.id);
  const cols = [...first, ...texts.slice(0, 6), ...nums.slice(0, 5)].slice(0, 13);
  return { dataset: main.dataset, calcs: main.calcs, kind: "table", columns: cols, filters, sort: date ? [{ column: date, dir: "desc" }] : [], limit: 200 };
}
